import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@common/encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { Address } from '../addresses/Address.js';
import { UitpasEventResponse } from '../endpoints/UitpasEventsResponse.js';
import { Image } from '../files/Image.js';
import { ReservedSeat } from '../SeatingPlan.js';
import { WebshopField } from './WebshopField.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
import { CustomerSettings } from './CustomerSettings.js';
export class ProductPrice extends AutoEncoder {
    /**
     * Bounds for the price a customer can choose when `allowCustomPrice` is enabled.
     * Enforced in the checkout UI and in `CartItem.refresh` (which also runs server side).
     */
    static customPriceMinimum = 1_00_00;
    static customPriceMaximum = 25_000_00_00;
    id;
    name = '';
    /**
     * Price is always in cents, to avoid floating point errors
     */
    price = 0;
    // Optional: different price if you reach a given amount of pieces (options and prices shouldn't be the same)
    discountPrice = null;
    // Only used if discountPrice is not null
    discountAmount = 2;
    hidden = false;
    allowCustomPrice = false;
    /**
     * Total stock, excluding already sold items into account
     */
    stock = null;
    usedStock = 0;
    /**
     * If this id is not null, this productPrice is the UiTPAS social tariff.
     * In that case it refers to the base price on which the UiTPAS social tariff is applied.
     * If it is null, this is not an UiTPAS social tariff.
     */
    uitpasBaseProductPriceId = null;
    get isSoldOut() {
        if (this.stock === null) {
            return false;
        }
        return this.usedStock >= this.stock;
    }
    get remainingStock() {
        if (this.stock === null) {
            return null;
        }
        return Math.max(0, this.stock - this.usedStock);
    }
    clearStock() {
        this.usedStock = 0;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductPrice.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], ProductPrice.prototype, "name", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], ProductPrice.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 93 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], ProductPrice.prototype, "discountPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 93 })
], ProductPrice.prototype, "discountAmount", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 219 })
], ProductPrice.prototype, "hidden", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 410 })
], ProductPrice.prototype, "allowCustomPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 221 })
], ProductPrice.prototype, "stock", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 221 })
], ProductPrice.prototype, "usedStock", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 376 })
], ProductPrice.prototype, "uitpasBaseProductPriceId", void 0);
export class Option extends AutoEncoder {
    id;
    name = '';
    /**
     * Price added (can be negative) is always in cents, to avoid floating point errors
     */
    price = 0;
    /**
     * Total stock, excluding already sold items into account
     */
    stock = null;
    usedStock = 0;
    get isSoldOut() {
        if (this.stock === null) {
            return false;
        }
        return this.usedStock >= this.stock;
    }
    get remainingStock() {
        if (this.stock === null) {
            return null;
        }
        return Math.max(0, this.stock - this.usedStock);
    }
    clearStock() {
        this.usedStock = 0;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Option.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Option.prototype, "name", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Option.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 221 })
], Option.prototype, "stock", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 221 })
], Option.prototype, "usedStock", void 0);
export class OptionMenu extends AutoEncoder {
    id;
    name = '';
    multipleChoice = false;
    /**
     * By default, we select the first option if multipleChoise is false.
     */
    autoSelectFirst = true;
    options = [
        Option.create({}),
    ];
    clearStock() {
        this.options.forEach(o => o.clearStock());
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], OptionMenu.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OptionMenu.prototype, "name", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], OptionMenu.prototype, "multipleChoice", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 376 })
], OptionMenu.prototype, "autoSelectFirst", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Option) })
], OptionMenu.prototype, "options", void 0);
export var ProductType;
(function (ProductType) {
    ProductType["Product"] = "Product";
    ProductType["Person"] = "Person";
    ProductType["Ticket"] = "Ticket";
    ProductType["Voucher"] = "Voucher";
})(ProductType || (ProductType = {}));
/**
 * This includes a location for a ticket (will be visible on the ticket)
 */
export class ProductLocation extends AutoEncoder {
    id;
    name = '';
    address = null;
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductLocation.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], ProductLocation.prototype, "name", void 0);
__decorate([
    field({ decoder: Address }),
    field({ decoder: Address, nullable: true, version: 146, downgrade: (v) => {
            if (!v) {
                return Address.createDefault();
            }
            return v;
        } })
], ProductLocation.prototype, "address", void 0);
/**
 * This includes a time for a ticket (will be visible on the ticket)
 */
export class ProductDateRange extends AutoEncoder {
    id;
    startDate = new Date();
    endDate = new Date();
    toString() {
        return Formatter.dateRange(this.startDate, this.endDate);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductDateRange.prototype, "id", void 0);
__decorate([
    field({ decoder: DateDecoder })
], ProductDateRange.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder })
], ProductDateRange.prototype, "endDate", void 0);
export class Product extends AutoEncoder {
    id;
    name = '';
    description = '';
    enabled = true;
    hidden = false;
    /**
     * Allow to order multiple pieces of the same product combination.
     * Null: follows the order mode of the webshop, see Webshop.allowsMultiple. Only older products have their own value.
     */
    allowMultiple;
    /**
     * Only allow one piece per product combination
     */
    unique = false;
    enableAfter = null;
    disableAfter = null;
    get isEnabled() {
        return this.enabled && !this.hidden && (!this.enableAfter || this.enableAfter <= new Date()) && (!this.disableAfter || this.disableAfter >= new Date());
    }
    get enableInFuture() {
        return this.enabled && !this.hidden && this.enableAfter !== null && this.enableAfter > new Date() && (!this.disableAfter || this.disableAfter >= new Date());
    }
    get isTicket() {
        return this.type === ProductType.Ticket || this.type === ProductType.Voucher;
    }
    images = [];
    customFields = [];
    type = ProductType.Product;
    location = null;
    dateRange = null;
    seatingPlanId = null;
    reservedSeats = [];
    /**
     * WIP: not yet supported
     * Set to true if you need to have a name for every ordered product. When this is true, you can't order this product mutliple times with the same name.
     * + will validate the name better
     */
    askName = false;
    /**
     * Collect customer details for every cart item of this product. Forces amount 1 per cart item.
     */
    enableCustomer = false;
    /**
     * null = default settings (only the name is asked)
     */
    customerSettings = null;
    get resolvedCustomerSettings() {
        return this.customerSettings ?? CustomerSettings.create({});
    }
    /**
     * Maximum amount per order
     */
    maxPerOrder = null;
    /**
     * Only show available stock if going below this amount - or null to always show the stock.
     */
    showStockBelow = 25;
    /**
     * Total stock, excluding already sold items into account
     */
    stock = null;
    usedStock = 0;
    /**
     * All the prices of this product (e.g. small, medium, large), should contain at least one price.
     */
    prices;
    optionMenus = [];
    /**
     * If this id is not null, we use the official UiTPAS flow.
     * If it is null, we use the non-official flow (if one of the productPrices is UiTPAS social tariff
     */
    uitpasEvent = null;
    clearStock() {
        this.usedStock = 0;
        this.reservedSeats = [];
        this.optionMenus.forEach(o => o.clearStock());
        this.prices.forEach(p => p.clearStock());
    }
    get isSoldOut() {
        return this.remainingStockWithOptions === 0;
    }
    /**
     * Only accounts for the stock of the product itself, not the stock of the options
     */
    get remainingStock() {
        if (this.stock === null) {
            return null;
        }
        return Math.max(0, this.stock - this.usedStock);
    }
    /**
     * Accounts for options and prices too
     */
    get remainingStockWithOptions() {
        const stocks = [];
        if (this.remainingStock !== null) {
            stocks.push(this.remainingStock);
        }
        let priceStocks = [];
        for (const price of this.prices) {
            if (price.remainingStock !== null) {
                priceStocks.push(price.remainingStock);
            }
            else {
                // Infinite stock for at least one price = no price stock
                priceStocks = [];
                break;
            }
        }
        if (priceStocks.length) {
            stocks.push(Math.max(...priceStocks));
        }
        for (const menu of this.optionMenus) {
            if (!menu.multipleChoice) {
                // Required to pick one
                // We need to pick the maximum of the option stock
                let menuStocks = [];
                for (const option of menu.options) {
                    if (option.remainingStock !== null) {
                        menuStocks.push(option.remainingStock);
                    }
                    else {
                        // Infinite stock for at least one option = no menu stock
                        menuStocks = [];
                        break;
                    }
                }
                if (menuStocks.length) {
                    stocks.push(Math.max(...menuStocks));
                }
            }
        }
        if (stocks.length === 0) {
            return null;
        }
        return Math.min(...stocks);
    }
    get hasAnyStock() {
        if (this.remainingStock !== null) {
            return true;
        }
        for (const price of this.prices) {
            if (price.remainingStock !== null) {
                return true;
            }
        }
        for (const menu of this.optionMenus) {
            // Required to pick one
            // We need to pick the maximum of the option stock
            for (const option of menu.options) {
                if (option.remainingStock !== null) {
                    return true;
                }
            }
        }
        return false;
    }
    getRemainingSeats(webshop, isAdmin) {
        if (this.seatingPlanId === null) {
            return null;
        }
        const seatingPlan = webshop.meta.seatingPlans.find(p => p.id === this.seatingPlanId);
        if (!seatingPlan) {
            return null;
        }
        if (isAdmin) {
            return seatingPlan.seatCount - this.reservedSeats.length;
        }
        return seatingPlan.seatCount - seatingPlan.adminSeatCount - this.reservedSeats.filter(r => !seatingPlan.isAdminSeat(r)).length;
    }
    /**
     * Whether it is not possibel to add multiple different items of this product to the cart, or whether this product supports multiple items in the cart.
     * Controls whether cart is edited by default or updated when clicking it open in the webshop.
     */
    get isUnique() {
        if (this.maxPerOrder === 1) {
            return true;
        }
        // Every person is a separate cart item
        if (this.enableCustomer) {
            return false;
        }
        // No choice options
        if (this.optionMenus.length === 0 && this.prices.length <= 1 && this.customFields.length === 0) {
            return true;
        }
        return false;
    }
    getRemainingStockText(stock) {
        if (stock === 1) {
            if (this.type === ProductType.Ticket) {
                return $t(`%t9`);
            }
            if (this.type === ProductType.Person) {
                return $t(`%tA`);
            }
            return $t(`%tB`);
        }
        if (this.type === ProductType.Ticket) {
            return stock + ' ' + $t(`%m`);
        }
        if (this.type === ProductType.Person) {
            return stock + ' ' + $t(`%12R`);
        }
        return stock + ' ' + $t(`%12S`);
    }
    get stockText() {
        if (this.remainingStockWithOptions === null || this.remainingStockWithOptions > 25) {
            return null;
        }
        if (this.remainingStockWithOptions === 0) {
            return $t(`%12p`);
        }
        return $t(`%Un`) + ' ' + this.getRemainingStockText(this.remainingStockWithOptions);
    }
    get isEnabledTextLong() {
        if (this.hidden) {
            return $t(`%UC`);
        }
        if (!this.enabled) {
            return $t(`%Tk`);
        }
        if (this.enableInFuture && this.enableAfter) {
            if (this.disableAfter) {
                return $t(`%tC`) + ' ' + Formatter.dateTime(this.enableAfter) + ' ' + $t(`%10C`) + ' ' + Formatter.dateTime(this.disableAfter);
            }
            return $t(`%tC`) + ' ' + Formatter.dateTime(this.enableAfter);
        }
        if (!this.isEnabled) {
            return $t(`%Tk`);
        }
        if (this.disableAfter) {
            return $t(`%tD`) + ' ' + Formatter.dateTime(this.disableAfter);
        }
    }
    get closesSoonText() {
        if (!this.isEnabled) {
            return null;
        }
        if (this.disableAfter) {
            const diff = this.disableAfter.getTime() - new Date().getTime();
            if (diff < 24 * 60 * 60 * 1000) {
                return $t(`%tD`) + ' ' + Formatter.time(this.disableAfter);
            }
        }
        return null;
    }
    filteredPrices(options) {
        if (options.admin) {
            return this.prices;
        }
        return this.prices.filter(p => !p.hidden);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Product.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Product.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Product.prototype, "description", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], Product.prototype, "enabled", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 172 })
], Product.prototype, "hidden", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 173, defaultValue: () => true }),
    field({
        decoder: BooleanDecoder,
        nullable: true,
        version: 424,
        downgrade: (allowMultiple) => allowMultiple ?? true,
        defaultValue: () => null,
    })
], Product.prototype, "allowMultiple", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 173 })
], Product.prototype, "unique", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 172 })
], Product.prototype, "enableAfter", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 172 })
], Product.prototype, "disableAfter", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Image) })
], Product.prototype, "images", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopField), version: 94 })
], Product.prototype, "customFields", void 0);
__decorate([
    field({ decoder: new EnumDecoder(ProductType) })
], Product.prototype, "type", void 0);
__decorate([
    field({ decoder: ProductLocation, nullable: true, version: 105 })
], Product.prototype, "location", void 0);
__decorate([
    field({ decoder: ProductDateRange, nullable: true, version: 105 })
], Product.prototype, "dateRange", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 211 })
], Product.prototype, "seatingPlanId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(ReservedSeat), nullable: true, version: 211 })
], Product.prototype, "reservedSeats", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], Product.prototype, "askName", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 425 })
], Product.prototype, "enableCustomer", void 0);
__decorate([
    field({ decoder: CustomerSettings, nullable: true, version: 425 })
], Product.prototype, "customerSettings", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 171 })
], Product.prototype, "maxPerOrder", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, optional: true })
], Product.prototype, "showStockBelow", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], Product.prototype, "stock", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Product.prototype, "usedStock", void 0);
__decorate([
    field({
        decoder: new ArrayDecoder(ProductPrice),
        defaultValue: () => [
            ProductPrice.create({
                name: '',
                price: 0,
            }),
        ],
    })
], Product.prototype, "prices", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OptionMenu) })
], Product.prototype, "optionMenus", void 0);
__decorate([
    field({ decoder: UitpasEventResponse, nullable: true, version: 378 })
], Product.prototype, "uitpasEvent", void 0);
//# sourceMappingURL=Product.js.map